<script setup lang="ts">
import { computed, ref } from 'vue';
import type { QTable } from 'quasar';
import type { TypedColumns } from '@/types/QuasarOverrides';
import AddressDisplay from '@/components/Utilities/AddressDisplay.vue';
import AdminPhoneDisplay from '@/components/Utilities/AdminPhoneDisplay.vue';
import type { EmailSelectionOption, FlightSchoolAdmin } from '@/types/FlightSchoolAdmin';
import TablePagination from '@/components/Utilities/TablePagination.vue';

export type AdminRow = Omit<FlightSchoolAdmin, 'emailSubscriber'> & {
    emailSubscriber: EmailSelectionOption;
};

const props = defineProps<{
    rows: AdminRow[],
    columns: TypedColumns,
    loading: boolean,
    selection?: 'single' | 'multiple',
    selected: AdminRow[],
}>();

/**
 * Todo: change to vue 3.3+ alternative to use more succinct syntax
 * @see: https://vuejs.org/guide/typescript/composition-api.html#typing-component-emits
 */
/* eslint-disable-next-line no-spaced-func, func-call-spacing */
const emit = defineEmits<{
    (event: 'update:selected', val: AdminRow[]): void;
    (event: 'update:subscribed', admin: AdminRow, subscribed: boolean): void;
    (event: 'delete:admins', admins: AdminRow[]): void;
}>();

const selected = computed({
    get: () => props.selected,
    set: (val: AdminRow[]) => {
        emit('update:selected', val);
    },
});

const updateSubscription = (row: AdminRow, option: EmailSelectionOption) => {
    emit('update:subscribed', row, option.value);
};

const emailSelectionOptions: EmailSelectionOption[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
];

const noAdminsTooltip = ref<boolean>(false);
const toggleNoAdmins = (delay: number) => {
    setTimeout(() => {
        noAdminsTooltip.value = false;
    }, delay);

    noAdminsTooltip.value = true;
};
const deleteAdmins = (admins: AdminRow[]) => {
    if (admins.length === 0) {
        toggleNoAdmins(1500);
        return;
    }
    emit('delete:admins', admins);
};

const adminsTable = ref<QTable>();
const pagination = ref({
    page: 1,
    rowsPerPage: 15,
});

const tablePagination = ref<typeof TablePagination>();

/**
 * Selects or unselects all rows that are not the owner.
 * @param value - true to select all, false to unselect all
 * @param computedRows - the rows to select or unselect. Determined by the
 * pagination, sorting, and filtering of the table.
 * See: https://quasar.dev/vue-components/table#qtable-api - computedRows
 */
const selectUnselectRows = (value: boolean, computedRows: AdminRow[]) => {
    const selectableRows = computedRows.filter((row) => row.isOwner !== true);
    selected.value = (value === false) ? selectableRows : [];
};

const quickFilter = ref<string|null>('');

const filterRows = (rows: AdminRow[]): AdminRow[] => {
    if (quickFilter.value === '' || quickFilter.value === null) {
        return rows;
    }

    const filter = quickFilter.value.toLowerCase();

    return rows.filter((row) => row.name.toLowerCase().includes(filter)
        || row.email.toLowerCase().includes(filter)
        || row.id.toString().includes(filter));
};

const filteredRows = computed(() => filterRows(props.rows));
</script>

<template>
    <!-- eslint-disable max-len -->
    <q-table
        ref="adminsTable"
        v-model:pagination="pagination"
        v-model:selected="selected"
        :selection="selection"
        :rows="filteredRows"
        :columns="columns"
        :loading="loading"
        row-key="id"
        class="fsum-admins-table bg-fsum-all-blue"
        table-header-class="bg-fsum-all-blue text-fsum-all-blue text-bold"
        table-class="bg-white"
        hide-pagination
        no-data-label="No Admins found"
    >
        <template
            v-if="rows.length > 0"
            #top
        >
            <div class="row justify-between full-width q-pb-md">
                <div
                    class="quick-filter print-hide"
                >
                    <q-input
                        v-model="quickFilter"
                        label="Search by name, email, or ID"
                        class="bg-white"
                        dense
                        outlined
                        clearable
                    >
                        <template #append>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                </div>
                <q-btn
                    flat
                    dense
                    label="Delete"
                    class="text-fsum-all-blue"
                    no-caps
                    @click="deleteAdmins(selected)"
                >
                    <q-tooltip
                        v-model="noAdminsTooltip"
                        no-parent-event
                        class="fsum-tooltip"
                    >
                        Please select at least one Administrator.
                    </q-tooltip>
                </q-btn>
            </div>
        </template>
        <template #header-selection="selectionProps">
            <q-checkbox
                v-model="selectionProps.selected"
                @update:model-value="selectUnselectRows(selectionProps.selected, adminsTable?.computedRows as AdminRow[])"
            />
        </template>
        <template #body-selection="selectionProps">
            <q-td>
                <q-checkbox
                    v-if="selectionProps.row.isOwner !== true"
                    v-model="selectionProps.selected"
                />
            </q-td>
        </template>
        <template #body-cell-role="isOwnerProps">
            <q-td align="left">
                {{ isOwnerProps.value ? 'Primary Administrator' : 'Administrator' }}
            </q-td>
        </template>
        <template #body-cell-address="addressProps">
            <q-td align="left">
                <AddressDisplay
                    :address="addressProps.value"
                />
            </q-td>
        </template>
        <template #body-cell-phoneNumbers="phoneNumbersProps">
            <q-td align="left">
                <AdminPhoneDisplay
                    :phone-numbers="phoneNumbersProps.value"
                />
            </q-td>
        </template>
        <template #body-cell-emailSubscriber="emailSubscriberProps">
            <q-td>
                <q-select
                    v-model="emailSubscriberProps.row.emailSubscriber"
                    :options="emailSelectionOptions"
                    outlined
                    class="w-50"
                    @update:model-value="(value: EmailSelectionOption) => updateSubscription(emailSubscriberProps.row, value)"
                />
            </q-td>
        </template>
        <template #bottom>
            <table-pagination
                ref="tablePagination"
                :pagination="pagination"
                :num-rows="filteredRows.length"
                pagination-label="Admins per page"
                @update:pagination="(newPagination) => pagination = newPagination"
            />
        </template>
    </q-table>
</template>

<style scoped>
.w-50 {
    width: 50%;
}
</style>

<style>
</style>
